import { useEffect, useState, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuickEnquiry } from '@/hooks/QuickEnquiryContext';
import InquirySection from './InquirySection';

const stats = [
  { icon: 'ri-user-star-line', value: '100M+', label: 'Subscribers', sub: 'Across all channels' },
  { icon: 'ri-eye-line', value: '100B+', label: 'Total Views', sub: 'And counting' },
  { icon: 'ri-award-line', value: '50+', label: 'YouTube Awards', sub: 'Gold & Silver' },
];

const quickServices = [
  'YouTube Services',
  'Music Production',
  'Audio Recording',
  'Video Production',
  'Artist Management',
  'Digital Promotion',
];

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const [count, setCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { openEnquiry } = useQuickEnquiry();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCount((c) => (c + 1) % 3);
    }, 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          alt="Ganga Bhakti Music Studio cinematic background"
          className="w-full h-full object-cover object-top"
          src="https://readdy.ai/api/search-image?query=cinematic%20dark%20Indian%20music%20recording%20studio%20interior%20with%20golden%20ambient%20light%20rays%20streaming%20through%20windows%2C%20vintage%20musical%20instruments%20sitar%20tabla%20harmonium%20arranged%20artistically%2C%20dramatic%20chiaroscuro%20lighting%2C%20luxury%20music%20production%20environment%2C%20rich%20warm%20amber%20tones%2C%20smoke%20haze%2C%20professional%20photography%2C%20ultra%20detailed&width=1920&height=1080&seq=hero001&orientation=landscape"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/60 via-transparent to-transparent"></div>
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-[#d4af37] animate-pulse"
            style={{
              width: i % 3 === 0 ? '3px' : '2px',
              height: i % 3 === 0 ? '3px' : '2px',
              left: `${5 + i * 8}%`,
              top: `${10 + (i % 5) * 18}%`,
              opacity: 0.2 + (i % 4) * 0.1,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2 + i * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Gold line decoration */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#d4af37]/40 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-28 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left — headline + CTAs */}
          <div className="max-w-2xl">
            <div className={`inline-flex items-center gap-3 mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <span className="w-10 h-px bg-[#d4af37]"></span>
              <span className="text-[#d4af37] text-xs font-semibold tracking-[4px] uppercase font-heading">
                Since 1992 — India's #1 Music Powerhouse
              </span>
            </div>

            <h1 className={`font-heading text-5xl md:text-7xl font-bold leading-[1.1] mb-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <span className="text-white">India's Leading</span>
              <br />
              <span className="text-[#d4af37]">Music Label</span>
              <br />
              <span className="text-white/90 text-3xl md:text-4xl lg:text-5xl font-medium">
                &amp; Digital Entertainment Network
              </span>
            </h1>

            <p className={`text-white/70 text-lg md:text-xl leading-relaxed mb-10 max-w-xl transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              Empowering artists, creating legends, and dominating digital music since 1992. From Bhakti to Bhojpuri — we are the authentic voice of India.
            </p>

            <div className={`flex flex-col sm:flex-row gap-4 mb-16 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-black whitespace-nowrap transition-all duration-300 hover:scale-105 btn-gold cursor-pointer">
                Work With Us <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-white whitespace-nowrap border border-[#d4af37]/50 hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer">
                Explore Services <i className="ri-play-circle-line"></i>
              </Link>
            </div>

            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              {stats.map((stat, i) => (
                <div key={stat.label} className={`glass-card rounded-2xl p-5 flex items-center gap-4 transition-all duration-500 ${count === i ? 'border-[#d4af37]/50 bg-[#d4af37]/5' : ''}`}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 bg-[#d4af37]/10">
                    <i className={`${stat.icon} text-2xl text-[#d4af37]`}></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold font-heading text-[#d4af37]">{stat.value}</div>
                    <div className="text-white text-sm font-semibold">{stat.label}</div>
                    <div className="text-white/40 text-xs">{stat.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — WhatsApp Quick Enquiry Card */}
          <div  className="hidden lg:block">
          <InquirySection visible={true}/>
          </div>
          
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-[#d4af37]"></i>
      </div>
    </section>
  );
}
